import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('eulogy');
export interface EulogyData {
  id?: string;
  // Eulogy content fields
  eulogyPreference?: string;
  preferredSpeaker?: string;
  speakerContact?: string;
  keyThemes?: string;
  importantStories?: string;
  accomplishments?: string;
  personalQualities?: string;
  relationships?: string;
  avoidTopics?: string;
  tonePreference?: string;
  lengthPreference?: string;
  specialRequests?: string;
  notes?: string;
  // Writer info fields
  writerName?: string;
  writerRelationship?: string;
  contactPhone?: string;
  contactEmail?: string;
  backupWriter?: string;
  backupContact?: string;
  writingStyle?: string;
  collaborationPreference?: string;
  deadlinePreference?: string;
  sourceMaterials?: string;
  specialInstructions?: string;
  writerNotes?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateEulogyRequest {
  eulogyPreference?: string;
  preferredSpeaker?: string;
  speakerContact?: string;
  keyThemes?: string;
  importantStories?: string;
  accomplishments?: string;
  personalQualities?: string;
  relationships?: string;
  avoidTopics?: string;
  tonePreference?: string;
  lengthPreference?: string;
  specialRequests?: string;
  notes?: string;
  writerName?: string;
  writerRelationship?: string;
  contactPhone?: string;
  contactEmail?: string;
  backupWriter?: string;
  backupContact?: string;
  writingStyle?: string;
  collaborationPreference?: string;
  deadlinePreference?: string;
  sourceMaterials?: string;
  specialInstructions?: string;
  writerNotes?: string;
}

export interface UpdateEulogyRequest extends CreateEulogyRequest {}

export const eulogyService = {
  createEulogy: async (data: CreateEulogyRequest): Promise<EulogyData> => {
    try {
      logger.debug('[Service] Creating eulogy with data:', data);
      const response = await http.post('/api/eulogy', data);
      logger.debug('[Service] Create response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error creating eulogy:', error);
      throw error;
    }
  },

  updateEulogy: async (id: string, data: UpdateEulogyRequest): Promise<EulogyData> => {
    try {
      logger.debug('[Service] Updating eulogy with ID:', id, 'data:', data);
      const response = await http.patch(`/api/eulogy/${id}`, data);
      logger.debug('[Service] Update response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error updating eulogy:', error);
      throw error;
    }
  },

  getAllEulogy: async (): Promise<EulogyData[]> => {
    try {
      logger.debug('[Service] Getting all eulogy info');
      const response = await http.get('/api/eulogy');
      logger.debug('[Service] Get all response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error getting eulogy info:', error);
      throw error;
    }
  },

  getEulogyById: async (id: string): Promise<EulogyData> => {
    try {
      logger.debug('[Service] Getting eulogy by ID:', id);
      const response = await http.get(`/api/eulogy/${id}`);
      logger.debug('[Service] Get by ID response:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('[Service] Error getting eulogy by ID:', error);
      throw error;
    }
  },

  deleteEulogy: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Deleting eulogy with ID:', id);
      await http.delete(`/api/eulogy/${id}`);
      logger.debug('[Service] Delete completed');
    } catch (error: any) {
      logger.error('[Service] Error deleting eulogy:', error);
      throw error;
    }
  },

  lockEulogy: async (id: string): Promise<void> => {
    try {
      logger.debug('[Service] Locking eulogy with ID:', id);
      await http.post(`/api/eulogy/${id}/lock`);
      logger.debug('[Service] Lock completed');
    } catch (error: any) {
      logger.error('[Service] Error locking eulogy:', error);
      throw error;
    }
  },

  unlockEulogy: async (id: string, granteeEmails: string[]): Promise<void> => {
    try {
      logger.debug('[Service] Unlocking eulogy with ID:', id, 'for emails:', granteeEmails);
      await http.post(`/api/eulogy/${id}/unlock`, { granteeEmails });
      logger.debug('[Service] Unlock completed');
    } catch (error: any) {
      logger.error('[Service] Error unlocking eulogy:', error);
      throw error;
    }
  },

  getEulogyAccessGrants: async (id: string): Promise<any[]> => {
    try {
      logger.debug('[Service] Getting access grants for eulogy with ID:', id);
      const response = await http.get(`/api/eulogy/${id}/access-grants`);
      logger.debug('[Service] Access grants response:', response.data);
      return response.data.data || [];
    } catch (error: any) {
      logger.error('[Service] Error getting eulogy access grants:', error);
      throw error;
    }
  },
};